import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { CHANGE_EVENT_LOCAL_KEY } from "@/utils/agenda.ts";
import { GetChangedEventResponse } from "@/types";
import { useMe, useUserSetChangeEventsMutation } from "@/data/user.ts";
import ROUTES from "@/config/routes.ts";
import StepOne from "@/components/agenda/change-event/mobile/steps/StepOne.tsx";
import StepTwo from "@/components/agenda/change-event/mobile/steps/StepTwo.tsx";
import StepThree from "@/components/agenda/change-event/mobile/steps/StepThree.tsx";

interface AgendaChangeEventMobileProps {
  response: GetChangedEventResponse;
  eventParams: string &
    Record<"user", unknown> &
    Record<"event", unknown> &
    Record<"events", unknown> &
    Record<"expire", unknown>;
}

const AgendaChangeEventMobile = ({
  response,
  eventParams,
}: AgendaChangeEventMobileProps) => {
  const navigate = useNavigate();
  const { me } = useMe();

  const handleClose = () => {
    localStorage.removeItem(CHANGE_EVENT_LOCAL_KEY);
    navigate(ROUTES.agenda, { replace: true });
  };

  const { mutate, isLoading } = useUserSetChangeEventsMutation(handleClose);

  const [step, setStep] = useState(1);
  const [eventSelected, setEventSelected] = useState(0);

  const eventSelect = useMemo(
    () => response.events.find((f) => f.id === Number(eventSelected)),
    [eventSelected, response.events],
  );

  const handleNext = () => setStep((r) => r + 1);
  const handleBack = () => setStep((r) => r - 1);

  const handleConfirm = () => {
    if (!me) return;
    mutate({
      userToken: me.item.user.token,
      event: eventParams.event as number,
      newEvent: Number(eventSelected),
    });
  };

  if (!response || !eventParams) return <Navigate to={ROUTES.agenda} />;

  return (
    <>
      {step === 1 && (
        <StepOne {...response} onNext={handleNext} onClose={handleClose} />
      )}

      {step === 2 && (
        <StepTwo
          {...response}
          onNext={handleNext}
          onBack={handleBack}
          eventSelect={eventSelected}
          onSelect={setEventSelected}
        />
      )}
      
      {step === 3 && (
        <StepThree
          onConfirm={handleConfirm}
          event={eventSelect}
          onBack={handleBack}
          loading={isLoading}
        />
      )}
    </>
  );
};

export default AgendaChangeEventMobile;
